import cn from "classnames";
import dayjs, { Dayjs } from "dayjs";

import { useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";

import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { LoadingButton } from "@mui/lab";
import { Chip, Fab, Grid, IconButton, Typography } from "@mui/material";

import ProChip from "@/components/ProChip";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import { MAX_ROUTE_CITY_COUNT } from "@/constants/config";
import { DATA_FAST_FORWARD } from "@/constants/eventName";
import {
  DEFAULT_DEALS_FLIGHT_SEARCH_DAYS,
  FREE_PANORAMA_RANGE,
  FREE_PANORAMA_SEARCH_DAYS,
  MAX_DEALS_FLIGHT_SEARCH_DAYS,
} from "@/constants/membership";
import { MAX_PANORAMA_SEARCH_DAYS, MEMBERSHIP_CONFIG } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePlatform from "@/hooks/usePlatform";

import useCondition, { ConditionType } from "../../hooks/useCondition";
import SelectLocation from "../SelectLocation";

interface SearchBarProps {
  isLoading?: boolean;
  onSearch?: (data: ConditionType, isClick?: boolean) => void;
  onChange?: (data: ConditionType) => void;
}

type RangeDate = [Dayjs | null, Dayjs | null];

const SearchBar = ({ onChange, onSearch, isLoading = false }: SearchBarProps) => {
  const { isMobile } = usePlatform();
  const { isDealsFlight, condition, setCondition, onLocationSwapChange, onDateChange } =
    useCondition();

  console.log("condition", isDealsFlight);
  const store = useGlobalStoreContext();
  const route_range = store?.route_range || MEMBERSHIP_CONFIG["routes_range"][0];
  const show_upsell = store?.show_upsell || false;
  const route_calendar_range =
    store?.route_calendar_range || MEMBERSHIP_CONFIG["route_calendar_range"][0];
  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  const datePickerRef = useRef<{ handleChange: (v: RangeDate) => void }>(null);

  const isFastForwardRef = useRef(false);

  useEffect(() => {
    onChange && onChange(condition);
  }, [condition, onChange]);

  useListenEvent(DATA_FAST_FORWARD, (v: RangeDate) => {
    datePickerRef.current?.handleChange(v);
    isFastForwardRef.current = true;
  });

  useEffect(() => {
    if (isFastForwardRef.current) {
      isFastForwardRef.current = false;
      onSearch && onSearch(condition, true);
    }
  }, [condition, onSearch]);

  const SearchBtn = useMemo(() => {
    return !isMobile ? (
      <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
        <LoadingButton
          color="primary"
          loading={isLoading}
          variant="contained"
          className="!min-w-[48px] !h-[56px] !w-[56px]"
          onClick={() => onSearch && onSearch(condition, true)}
        >
          <SearchIcon sx={{ fontSize: "24px" }} />
        </LoadingButton>
      </div>
    ) : null;
  }, [isLoading, isMobile, onSearch, condition]);

  return (
    <div>
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{ paddingRight: !isMobile ? "64px" : 0 }}
      >
        <Grid item xs={12} sm={8.2}>
          <Grid container alignItems="center" rowGap={2}>
            <Grid item xs={12} sm={5.6}>
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where from?"
                Icon={<FiberManualRecordOutlinedIcon />}
                selectData={condition.fromLocation}
                onChange={(data) => {
                  setCondition({ ...condition, fromLocation: data });
                }}
              />
            </Grid>

            {!isMobile ? (
              <Grid item xs={1} sm={0.8} alignItems="center">
                <div className="flex items-center justify-center">
                  <IconButton
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                    disabled={disableSwap}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </div>
              </Grid>
            ) : (
              <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                <Fab color="primary" aria-label="swap" size="small" onClick={onLocationSwapChange}>
                  <SwapHorizIcon className="rotate-90" />
                </Fab>
              </div>
            )}

            <Grid item xs={12} sm={5.6}>
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where to?"
                Icon={<LocationOnOutlinedIcon />}
                selectData={condition.toLocation}
                onChange={(data) => {
                  setCondition({ ...condition, toLocation: data });
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={3.8}>
          <SearchRangeDate
            ref={datePickerRef}
            label="Date Range"
            defaultValue={[
              condition.startDate ? dayjs(condition.startDate) : null,
              condition.endDate ? dayjs(condition.endDate) : null,
            ]}
            customToolbar={
              show_upsell ? (
                <>
                  <Typography style={{ fontSize: isMobile ? "15px" : "16px" }}>
                    {"Free users can search up to " +
                      FREE_PANORAMA_RANGE +
                      " days with " +
                      FREE_PANORAMA_SEARCH_DAYS +
                      "-day range."}
                  </Typography>
                  <Typography
                    style={{ fontSize: isMobile ? "15px" : "16px" }}
                    fontWeight={900}
                    color="primary"
                  >
                    Upgrade to
                    <ProChip hasRightMargin />
                    {"to search 365 days with " + MAX_PANORAMA_SEARCH_DAYS + "-day range."}
                  </Typography>
                </>
              ) : (
                <Typography style={{ fontSize: isMobile ? "15px" : "16px" }} color="primary">
                  {"Enjoy searching 365 days with " + MAX_PANORAMA_SEARCH_DAYS + "-day range."}
                </Typography>
              )
            }
            onChange={onDateChange}
            maxRangeDays={isDealsFlight ? DEFAULT_DEALS_FLIGHT_SEARCH_DAYS : route_range}
            calendarRangeDays={isDealsFlight ? MAX_DEALS_FLIGHT_SEARCH_DAYS : route_calendar_range}
          />
        </Grid>

        {SearchBtn}
      </Grid>

      {isMobile ? (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            loading={isLoading}
            startIcon={<SearchIcon />}
            onClick={() => onSearch && onSearch(condition, true)}
          >
            Search
          </LoadingButton>
        </div>
      ) : null}
    </div>
  );
};

export default SearchBar;
